import { useState } from "react";
import axios from "axios";
import { useAuth, API } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UserPlus, Pencil, Trash2, Users, ShieldCheck, RefreshCcw } from "lucide-react";

const NEW_USER = { email: "", password: "", name: "", role: "operator", permits_quota: 10 };

export default function UsersManager({ users, onReload }) {
  const { authHeaders, user: currentUser } = useAuth();
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newUser, setNewUser] = useState(NEW_USER);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Completa todos los campos");
      return;
    }
    if (newUser.password.length < 6) {
      toast.error("La contrase\u00f1a debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users`, newUser, { headers: authHeaders });
      toast.success("Usuario creado correctamente");
      setOpenCreate(false);
      setNewUser(NEW_USER);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear usuario");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editing) return;
    setLoading(true);
    try {
      const update = {
        name: editing.name,
        role: editing.role,
        permits_quota: parseInt(editing.permits_quota || 0),
        active: editing.active,
      };
      if (editing.newPassword) update.password = editing.newPassword;
      await axios.put(`${API}/admin/users/${editing.id}`, update, { headers: authHeaders });
      toast.success("Usuario actualizado");
      setEditing(null);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar usuario");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar al usuario ${u.email}? Esta acci\u00f3n no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { headers: authHeaders });
      toast.success("Usuario eliminado");
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <Card>
      <CardHeader style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <CardTitle style={{ display: "flex", alignItems: "center", gap: 8 }}><Users size={18} color="#f58238" /> Gestión de usuarios</CardTitle>
          <CardDescription>Crea usuarios y asigna la cantidad de permisos que pueden emitir</CardDescription>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="outline" size="sm" onClick={onReload}><RefreshCcw size={14} style={{ marginRight: 6 }} /> Actualizar</Button>
          <Button onClick={() => setOpenCreate(true)} style={{ backgroundColor: "#f58238", color: "white" }}>
            <UserPlus size={14} style={{ marginRight: 6 }} /> Nuevo usuario
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
            <thead>
              <tr style={{ backgroundColor: "#f9fafb", borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
                <th style={th}>Nombre</th>
                <th style={th}>Email</th>
                <th style={th}>Rol</th>
                <th style={th}>Permisos asignados</th>
                <th style={th}>Usados</th>
                <th style={th}>Disponibles</th>
                <th style={th}>Estado</th>
                <th style={th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const remaining = Math.max((u.permits_quota || 0) - (u.permits_used || 0), 0);
                return (
                  <tr key={u.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={td}><b>{u.name}</b></td>
                    <td style={td}>{u.email}</td>
                    <td style={td}>
                      {u.role === "admin" ? (
                        <Badge style={{ backgroundColor: "#f58238", color: "white" }}><ShieldCheck size={12} style={{ marginRight: 4 }} />Admin</Badge>
                      ) : (
                        <Badge variant="secondary">Usuario</Badge>
                      )}
                    </td>
                    <td style={td}>{u.role === "admin" ? "∞" : u.permits_quota}</td>
                    <td style={td}>{u.permits_used}</td>
                    <td style={td}>{u.role === "admin" ? "∞" : <b style={{ color: remaining > 0 ? "#10b981" : "#ef4444" }}>{remaining}</b>}</td>
                    <td style={td}>{u.active === false ? <Badge variant="destructive">Inactivo</Badge> : <Badge style={{ backgroundColor: "#10b981", color: "white" }}>Activo</Badge>}</td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Button size="sm" variant="outline" onClick={() => setEditing({ ...u, newPassword: "" })}><Pencil size={14} /></Button>
                        {u.id !== currentUser?.id && (
                          <Button size="sm" variant="outline" onClick={() => handleDelete(u)} style={{ color: "#ef4444" }}><Trash2 size={14} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Dialog: Create */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear nuevo usuario</DialogTitle>
            <DialogDescription>El usuario podrá emitir permisos según la cuota asignada.</DialogDescription>
          </DialogHeader>
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <Label>Nombre completo</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
            </div>
            <div>
              <Label>Correo electrónico</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value.toLowerCase() })} />
            </div>
            <div>
              <Label>Contraseña (mínimo 6 caracteres)</Label>
              <Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label>Rol</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">Usuario</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Permisos asignados</Label>
                <Input type="number" min="0" value={newUser.permits_quota} onChange={(e) => setNewUser({ ...newUser, permits_quota: parseInt(e.target.value || 0) })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)} disabled={loading}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={loading} style={{ backgroundColor: "#f58238", color: "white" }}>
              {loading ? "Creando..." : "Crear usuario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
            <DialogDescription>{editing?.email}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <Label>Nombre completo</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <Label>Rol</Label>
                  <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operator">Usuario</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Permisos asignados</Label>
                  <Input type="number" min="0" value={editing.permits_quota} onChange={(e) => setEditing({ ...editing, permits_quota: parseInt(e.target.value || 0) })} />
                </div>
              </div>
              <div>
                <Label>Nueva contraseña (opcional)</Label>
                <Input type="text" value={editing.newPassword || ""} onChange={(e) => setEditing({ ...editing, newPassword: e.target.value })} placeholder="Dejar en blanco para no cambiar" />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" id="active" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} style={{ width: 16, height: 16 }} />
                <Label htmlFor="active" style={{ margin: 0, cursor: "pointer" }}>Usuario activo</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={loading}>Cancelar</Button>
            <Button onClick={handleUpdate} disabled={loading} style={{ backgroundColor: "#f58238", color: "white" }}>
              {loading ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const th = { padding: "0.6rem 0.75rem", fontWeight: 600, color: "#374151", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "0.7rem 0.75rem", verticalAlign: "top", color: "#1f2937" };
